import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import WarehouseRoundedIcon from '@mui/icons-material/WarehouseRounded';
import { Stack } from '@mui/material';

import { useInventorySettings, useUpdateInventorySettings } from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type {
  InventoryTrackingMode,
  OutOfStockBehavior,
  ReservationPolicy,
} from '../../model/settingsTypes';
import {
  FeedbackSnackbar,
  NumberField,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TextInputField,
} from '../components';
import {
  INVENTORY_TRACKING_MODE_OPTIONS,
  OUT_OF_STOCK_BEHAVIOR_OPTIONS,
  RESERVATION_POLICY_OPTIONS,
} from '../options';

/** Inventory settings: stock tracking, reservation and availability policy. */
export function InventorySettingsPage() {
  const { canView, canManage } = useDomainPermissions('inventory');
  const query = useInventorySettings();
  const update = useUpdateInventorySettings();
  const form = useSettingsForm(query.data);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success('Inventory settings saved.'),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title="Inventory settings"
      subtitle="Stock tracking, reservation policy and availability display."
      icon={<WarehouseRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title="Stock tracking" icon={<WarehouseRoundedIcon />}>
              <SelectField
                label="Tracking mode"
                value={draft.trackingMode}
                options={INVENTORY_TRACKING_MODE_OPTIONS}
                onChange={(value) => form.setField('trackingMode', value as InventoryTrackingMode)}
                disabled={disabled}
              />
              <TextInputField
                label="Default warehouse code"
                value={draft.defaultWarehouseCode ?? ''}
                onChange={(value) => form.setField('defaultWarehouseCode', value)}
                disabled={disabled}
              />
              <NumberField
                label="Low-stock threshold"
                description="Flag a product as low stock at or below this quantity."
                value={draft.lowStockThreshold}
                onChange={(value) => form.setField('lowStockThreshold', value)}
                disabled={disabled}
                min={0}
                suffix="units"
              />
            </SettingsCard>

            <SettingsCard title="Reservation" icon={<TuneRoundedIcon />}>
              <SelectField
                label="Reservation policy"
                value={draft.reservationPolicy}
                options={RESERVATION_POLICY_OPTIONS}
                onChange={(value) => form.setField('reservationPolicy', value as ReservationPolicy)}
                disabled={disabled}
              />
              <NumberField
                label="Reservation timeout"
                description="Release a reservation after this long without payment."
                value={draft.reservationTimeoutMinutes}
                onChange={(value) => form.setField('reservationTimeoutMinutes', value)}
                disabled={disabled}
                min={0}
                suffix="minutes"
              />
            </SettingsCard>

            <SettingsCard title="Availability" icon={<Inventory2RoundedIcon />}>
              <SwitchField
                label="Allow backorder"
                checked={draft.allowBackorder}
                onChange={(value) => form.setField('allowBackorder', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Allow preorder"
                checked={draft.allowPreorder}
                onChange={(value) => form.setField('allowPreorder', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Allow overselling"
                description="Permit orders beyond available stock."
                checked={draft.oversellEnabled}
                onChange={(value) => form.setField('oversellEnabled', value)}
                disabled={disabled}
              />
              <SelectField
                label="Out-of-stock behaviour"
                value={draft.outOfStockBehavior}
                options={OUT_OF_STOCK_BEHAVIOR_OPTIONS}
                onChange={(value) =>
                  form.setField('outOfStockBehavior', value as OutOfStockBehavior)
                }
                disabled={disabled}
              />
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
